<script setup lang="ts">
import { ref, onMounted, computed } from "vue";
import API from "@/modules/api";
import EmptyContent from "@/components/mobile/common/EmptyContent.vue";
import { showLoadingToast, showSuccessToast } from "vant";
import { useI18n } from "vue-i18n";
import { useGlobalUser } from "@/stores/globalUser";
import { copyToClipboard, getDisplayDateTime } from "@/modules/common";
import { useRouter } from "vue-router";
import { useMate } from "@/stores/mate";
import { ThreadListInstance } from "../mobile/ThreadList.vue";

const { t } = useI18n();
const userStore = useGlobalUser();
const router = useRouter();

const invitationList = ref<UserInvitationCode[]>([]);
const loading = ref(false);
const totalCount = ref(0);
const restCount = computed(() => {
  return (userStore.user?.invitableUsersCount ?? 0) - totalCount.value;
});

onMounted(async () => {
  await userStore.refreshUser();
  // await refreshInviteCodeList();
});

const threadList = ref<ThreadListInstance>();

const reload = async () => {
  invitationList.value = [];
  threadList.value?.resetScrollLoadingState();
};

const loadNextPage = async () => {
  const { length } = invitationList.value;
  const page = length / 20 + 1;
  if (length !== 0 && length % 20 !== 0) return false;
  const list = await refreshInviteCodeList(page);
  return list.length === 20;
};
const refreshInviteCodeList = async (page: number) => {
  const res = await API.readUserInvitationCodePage({
    pagination: {
      page,
      perPage: 20,
    },
    sort: {
      field: "dtCreate",
      order: "desc",
    },
  });
  invitationList.value = [...invitationList.value, ...res.page.data];
  if (page === 1) {
    totalCount.value = res.page.total ?? 0;
  }
  return res.page.data;
};
const generateInviteCode = async () => {
  const loadingToast = showLoadingToast({
    message: t("Message.loading"),
    duration: 0,
  });
  try {
    await API.createUserInvitationCode();
    reload();
  } finally {
    loadingToast.close();
  }
};

const copyInviteCode = (code: string) => {
  const mate = useMate().userMate.name;
  const link = `${location.origin}/login?mode=register&code=${code}`;
  copyToClipboard(
    `${t("Message.inviterMessage", { mate })}
${t("Message.inviterMessage2", { link })}`
  );
  showSuccessToast(t("Message.copySuccessful"));
};
</script>
<template>
  <PageLayout>
    <template #header>
      <threads-nav :show-center="false" :show-back="true">
        <template #right>
          <div class="font-bold">
            {{ $t("invitation.title") }}
          </div>
        </template>
      </threads-nav>
    </template>
    <ThreadList
      v-if="userStore.user.canInviteUser"
      ref="threadList"
      :on-pull-load="() => reload()"
      :on-scroll-load="() => loadNextPage()"
    >
      <div class="px-4 flex flex-col gap-4 overflow-y-auto h-full">
        <template v-if="loading">
          <div class="flex justify-center items-center h-full">
            <span class="loading"></span>
          </div>
        </template>
        <template v-else>
          <button
            class="btn w-full rounded"
            @click="generateInviteCode"
            :disabled="restCount <= 0"
          >
            <i class="iconify mingcute--add-fill" />
            {{ $t("invitation.generateInviteCode") }}({{
              $t("invitation.restCount", {
                count: restCount,
              })
            }})
          </button>
          <RedotTitle
            :title="$t('invitation.generatedInviteCode')"
            class="mx-4"
          />
          <div
            v-if="invitationList.length"
            class="flex flex-col rounded bg-base-200 mx-4 px-2"
          >
            <div
              v-for="item in invitationList"
              :key="item.id"
              class="flex justify-between items-center p-2 border-b last:border-b-0"
              @click="item.isUsed ? null : copyInviteCode(item.code)"
            >
              <div class="flex flex-col gap-1 justify-center flex-1">
                <div class="flex justify-between">
                  <span>{{ item.code }}</span>
                  <div
                    v-if="item.invitedUser"
                    class="flex flex-col justify-start h-full"
                  >
                    <button
                      class="btn btn-xs btn-outline"
                      @click="
                        router.push({
                          name: 'mateHome',
                          params: { uuid: item.invitedUser.userMate?.uuid },
                        })
                      "
                    >
                      {{ item.invitedUser.userMate?.name }}
                      <i class="iconify mingcute--right-small-line" />
                    </button>
                  </div>
                </div>
                <div v-if="item.isUsed" class="flex gap-1">
                  <div class="text-sm text-base-content/50">
                    {{ $t("invitation.used") }}
                  </div>
                  <div class="text-sm text-base-content/50">
                    {{ item.dtUpdate ? getDisplayDateTime(item.dtUpdate) : "" }}
                  </div>
                </div>
              </div>
              <div
                v-if="!item.isUsed"
                class="flex gap-1 flex-col justify-center"
              >
                <i class="iconify mingcute--copy-line" />
              </div>
            </div>
          </div>
          <EmptyContent
            v-else
            :title="$t('invitation.empty.title')"
            :desc="$t('invitation.empty.desc')"
          />
        </template>
      </div>
    </ThreadList>
    <div v-else>
      <EmptyContent
        :title="$t('invitation.cannotInviteUser.title')"
        :desc="$t('invitation.cannotInviteUser.desc')"
      />
    </div>
  </PageLayout>
</template>
